import { sleep } from "tm-shared/utils";

type VideoQuality = "4k" | "fullHd";

const VIDEO_QUALITY_PRESETS: Record<
    VideoQuality,
    Pick<MediaTrackConstraints, "width" | "height">
> = {
    "4k": {
        width: { ideal: 3840, min: 1280 },
        height: { ideal: 2160, min: 720 },
    },
    fullHd: {
        width: { ideal: 1920, min: 1280 },
        height: { ideal: 1080, min: 720 },
    },
};

export const getVideoConstraints = (
    selectedDeviceId: string | null,
    quality: VideoQuality = "fullHd",
): MediaTrackConstraints => {
    const resolution = VIDEO_QUALITY_PRESETS[quality];

    const base: MediaTrackConstraints = {
        aspectRatio: { ideal: 16 / 9 },
        frameRate: { ideal: 30, max: 30 },
        ...resolution,
    };

    return selectedDeviceId
        ? {
              ...base,
              deviceId: { ideal: selectedDeviceId },
          }
        : {
              ...base,
              facingMode: { ideal: "environment" },
          };
};

export const findCameraWithFlash = async (
    videoDevices: MediaDeviceInfo[],
): Promise<string | null> => {
    for (const device of videoDevices) {
        let testStream: MediaStream | null = null;

        try {
            testStream = await navigator.mediaDevices.getUserMedia({
                video: { deviceId: { exact: device.deviceId } },
            });

            const testTrack = testStream.getVideoTracks()[0];
            const caps =
                testTrack.getCapabilities() as MediaTrackCapabilities & {
                    torch?: boolean;
                };

            if (caps.torch) return device.deviceId;
        } catch (e) {
            console.warn(`Flash check failed for ${device.label}:`, e);
        } finally {
            testStream?.getTracks().forEach((t) => t.stop());
        }
    }

    return null;
};

type ExposureModeValue = "manual" | "continuous";
type FocusModeValue = "manual" | "single-shot" | "continuous";

type ExtendedSupportedConstraints = MediaTrackSupportedConstraints & {
    focusMode?: boolean;
    focusDistance?: boolean;
    exposureMode?: boolean;
    exposureCompensation?: boolean;
};

type ExtendedMediaTrackCapabilities = MediaTrackCapabilities & {
    focusMode?: FocusModeValue[];
    focusDistance?: MediaSettingsRange;
    exposureMode?: ExposureModeValue[];
    exposureCompensation?: MediaSettingsRange;
    torch?: boolean;
    pointOfInterest?: { x: MediaSettingsRange; y: MediaSettingsRange };
};

type ExtendedMediaTrackSettings = MediaTrackSettings & {
    focusDistance?: number;
    focusMode?: FocusModeValue;
    exposureMode?: ExposureModeValue;
    exposureCompensation?: number;
    pointOfInterest?: unknown;
};

type ExtendedMediaTrackConstraintSet = MediaTrackConstraintSet & {
    focusMode?: ConstrainDOMString;
    focusDistance?: ConstrainDouble;
    exposureMode?: ConstrainDOMString;
    exposureCompensation?: ConstrainDouble;
    pointOfInterest?: { x: number; y: number };
};

export const hasGetCapabilities = (
    track: MediaStreamTrack,
): track is MediaStreamTrack & {
    getCapabilities: () => ExtendedMediaTrackCapabilities;
} =>
    typeof (track as MediaStreamTrack & { getCapabilities?: unknown })
        .getCapabilities === "function";

const getExtendedCapabilities = (
    track: MediaStreamTrack,
): ExtendedMediaTrackCapabilities | undefined => {
    const maybeTrack = track as MediaStreamTrack & {
        getCapabilities?: () => MediaTrackCapabilities;
    };

    if (typeof maybeTrack.getCapabilities !== "function") {
        return undefined;
    }

    return maybeTrack.getCapabilities() as ExtendedMediaTrackCapabilities;
};

export const waitForFocus = async (
    track: MediaStreamTrack,
    skipTrigger = false,
): Promise<void> => {
    const supported =
        navigator.mediaDevices.getSupportedConstraints() as ExtendedSupportedConstraints;

    if (supported.focusMode && !skipTrigger) {
        try {
            const capabilities: ExtendedMediaTrackCapabilities | undefined =
                hasGetCapabilities(track)
                    ? (track.getCapabilities() as ExtendedMediaTrackCapabilities)
                    : undefined;

            const focusModes = capabilities?.focusMode ?? [];

            if (focusModes.includes("single-shot")) {
                await track.applyConstraints({
                    advanced: [
                        {
                            focusMode: "single-shot",
                        } as ExtendedMediaTrackConstraintSet,
                    ],
                });
                await sleep(80);
            } else if (focusModes.includes("continuous")) {
                await track.applyConstraints({
                    advanced: [
                        {
                            focusMode: "continuous",
                        } as ExtendedMediaTrackConstraintSet,
                    ],
                });
            }
        } catch (error) {
            console.warn("waitForFocus:applyConstraints failed", error);
        }
    }

    const canReadFocusDistance =
        !!supported.focusDistance && typeof track.getSettings === "function";

    if (!canReadFocusDistance) {
        await sleep(150);
        return;
    }

    let lastFocusDistance: number | undefined;
    let stableCount = 0;
    let focusStarted = false;
    let unchangedFromStart = 0;

    const STABLE_NEEDED = 2;
    const INTERVAL = 80;
    const UNCHANGED_EARLY_EXIT = 3;
    const MAX_WAIT = 2500;

    const startedAt = Date.now();

    while (Date.now() - startedAt < MAX_WAIT) {
        const settings = track.getSettings() as ExtendedMediaTrackSettings;
        const currentFocus = settings.focusDistance;

        if (typeof currentFocus !== "number" || Number.isNaN(currentFocus)) {
            await sleep(150);
            return;
        }

        if (lastFocusDistance === undefined) {
            lastFocusDistance = currentFocus;
            await sleep(INTERVAL);
            continue;
        }

        const changed = Math.abs(currentFocus - lastFocusDistance) > 0.01;

        if (changed) {
            focusStarted = true;
            stableCount = 0;
            unchangedFromStart = 0;
        } else if (focusStarted) {
            stableCount++;
        } else {
            unchangedFromStart++;
            if (unchangedFromStart >= UNCHANGED_EARLY_EXIT) {
                return;
            }
        }

        lastFocusDistance = currentFocus;

        if (focusStarted && stableCount >= STABLE_NEEDED) {
            return;
        }

        await sleep(INTERVAL);
    }
};

export const tapToFocus = async (
    track: MediaStreamTrack,
    x: number,
    y: number,
): Promise<void> => {
    const supported =
        navigator.mediaDevices.getSupportedConstraints() as ExtendedSupportedConstraints;

    if (!supported.focusMode) {
        return;
    }

    try {
        const caps: ExtendedMediaTrackCapabilities | undefined =
            hasGetCapabilities(track)
                ? (track.getCapabilities() as ExtendedMediaTrackCapabilities)
                : undefined;

        const focusModes = caps?.focusMode ?? [];
        const hasPointOfInterest =
            caps != null && "pointOfInterest" in caps && !!caps.pointOfInterest;

        if (
            !focusModes.includes("single-shot") &&
            !focusModes.includes("manual")
        ) {
            return;
        }

        const focusMode: FocusModeValue = focusModes.includes("single-shot")
            ? "single-shot"
            : "manual";

        const constraints = {
            focusMode,
            ...(hasPointOfInterest ? { pointOfInterest: { x, y } } : {}),
        } as ExtendedMediaTrackConstraintSet;

        await track.applyConstraints({ advanced: [constraints] });
    } catch (err) {
        console.warn("tapToFocus:failed", err);
    }
};

export const prepareExposure = async (
    track: MediaStreamTrack,
): Promise<void> => {
    const supported =
        navigator.mediaDevices.getSupportedConstraints() as ExtendedSupportedConstraints;

    if (!supported.exposureMode) {
        await sleep(120);
        return;
    }

    try {
        const caps = getExtendedCapabilities(track);
        const advanced: ExtendedMediaTrackConstraintSet = {};

        if (caps?.exposureMode?.includes("continuous")) {
            advanced.exposureMode = "continuous";
        }

        if (supported.exposureCompensation && caps?.exposureCompensation) {
            const { min, max } = caps.exposureCompensation;

            if (min <= 0 && max >= 0) {
                advanced.exposureCompensation = 0;
            }
        }

        if (Object.keys(advanced).length > 0) {
            await track.applyConstraints({
                advanced: [advanced],
            });
        }

        await sleep(180);
    } catch (err) {
        console.warn("prepareExposure failed", err);
        await sleep(120);
    }
};
